<template>
  <AppShell title="Skapa ny ansökan">
    <div class="grid gap-6">
      <AppCard>
        <section class="grid gap-4">
          <div>
            <h2>Välj typ av ansökan</h2>
          </div>

          <AppSelect
            v-model="selectedApplicationType"
            label="Ansökningstyp"
            :options="createApplicationTypeOptions"
            mandatory
          />
          <AppTextField
            v-if="selectedApplicationType === 'topupLoanApplication'"
            v-model="form.debtId"
            trim
            :rules="[required(), uuid()]"
            label="Debt ID"
            placeholder="UUID"
          />

          <ApplicationErrorBox v-if="error" :message="error" />

          <div class="flex justify-end">
            <AppButton type="button" :disabled="isCreatingDraft" color="green" large @click="createDraftAndOpen">
              {{ isCreatingDraft ? 'Skapar...' : 'Skapa ny ansökan' }}
            </AppButton>
          </div>
        </section>
      </AppCard>

      <AppCard>
        <section class="grid gap-4">
          <div class="flex flex-wrap items-baseline justify-between gap-3">
            <h2>Fortsätt med utkast</h2>
            <p v-if="!isLoadingDrafts" class="text-subtle">{{ draftApplications.length }} utkast</p>
          </div>
          <p class="text-subtle">Fortsätt med en tidigare sparad ansökan</p>

          <p v-if="isLoadingDrafts" class="text-subtle">Hämtar utkast...</p>
          <p v-else-if="draftError" class="error-text">{{ draftError }}</p>
          <div v-else-if="draftApplications.length">
            <AppTable expandable>
              <thead>
                <tr>
                  <th>Typ</th>
                  <th>Kund</th>
                  <th>Belopp</th>
                  <th>Senast uppdaterad</th>
                  <th>Handläggare</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                <AppTableRow
                  v-for="application in draftApplications"
                  :key="application.id"
                  @click="openDraft(application)"
                >
                  <AppTableCell>{{ translateRef('applicationTypes', application.type) }}</AppTableCell>
                  <AppTableCell>{{ getDraftApplicant(application) }}</AppTableCell>
                  <AppTableCell>
                    {{ formatAmountWithCurrency(application.amountAppliedFor) }}
                  </AppTableCell>
                  <AppTableCell>{{ formatDateTimeValue(application.updatedAt) }}</AppTableCell>
                  <AppTableCell>
                    {{ getPersonnelNameByUserId(application.brokerPersonnelUserId) || '-' }}
                  </AppTableCell>
                  <AppTableCell>
                    <AppButton
                      type="button"
                      color="red"
                      size="small"
                      :disabled="!application.id"
                      @click.stop="application.id && openDeleteDraftModal(application.id, application.type)"
                    >
                      Ta bort
                    </AppButton>
                  </AppTableCell>
                </AppTableRow>
              </tbody>
            </AppTable>
          </div>
          <p v-else class="text-subtle">Inga utkast hittades.</p>
        </section>
      </AppCard>
    </div>
  </AppShell>
</template>

<script setup lang="ts">
import { formatAmountWithCurrency, formatDateTimeValue, required, uuid } from '@staff/shared'
import { AppButton, AppCard, AppSelect, AppTable, AppTableCell, AppTableRow, AppTextField, useModal } from '@staff/ui'
import AppShell from '@/components/AppShell.vue'
import ConfirmDeleteDraftModal from '@/components/ConfirmDeleteDraftModal.vue'
import ApplicationErrorBox from './components/ApplicationErrorBox.vue'
import { useMetadata } from '@/composables/useMetadata'
import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'
import { useCreateApplicationDraft } from './composables/useCreateApplicationDraft'
import { useApplicationFormData } from './composables/applicationFormData'
import type { ApplicationType } from '@/services/application'

const {
  isCreatingDraft,
  isLoadingDrafts,
  error,
  draftError,
  draftApplications,
  selectedApplicationType,
  createApplicationTypeOptions,
  getDraftApplicant,
  loadDraftApplications,
  openDraft,
  createDraftAndOpen,
} = useCreateApplicationDraft()

const { translateRef } = useMetadata()
const { getPersonnelNameByUserId } = usePersonnelDirectory()
const { form } = useApplicationFormData()

const { openModal } = useModal()

const openDeleteDraftModal = async (applicationId: string, applicationType: ApplicationType) => {
  openModal('Ta bort utkast', ConfirmDeleteDraftModal, {
    applicationId,
    applicationType,
    onSuccess: loadDraftApplications,
  })
}
</script>
